/**
 * Shared primitives for the marketing landing (`/`), modelled on plane.so.
 * Everything here is light-only and scoped under `.mk` (see styles.css) so the app's `.dark` never leaks in.
 * Sections live in `sections-top.tsx` / `sections-bottom.tsx`; this file only holds the vocabulary they share.
 */
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import type { LinkProps } from '@tanstack/react-router'
import { ArrowRight, ChevronDown, Menu, X } from 'lucide-react'
import { Wordmark } from '#/components/layout/wordmark'
import { authClient, useHydratedSession } from '#/lib/auth-client'
import { cn } from '#/lib/utils'
import { getLocale, setLocale } from '#/paraglide/runtime'
import { m } from '#/paraglide/messages'

export function Container({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return <div className={cn('mk-container', className)}>{children}</div>
}

export function Overline({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return <p className={cn('mk-overline', className)}>{children}</p>
}

/** plane.so section header: overline → H2 → lead → CTA pair. `align="left"` is the two-column variant (capabilities, self-host). */
export function SectionHead({
  overline,
  title,
  lead,
  actions,
  align = 'center',
  className,
  id,
}: {
  overline?: ReactNode
  title: ReactNode
  lead?: ReactNode
  actions?: ReactNode
  align?: 'center' | 'left'
  className?: string
  id?: string
}) {
  if (align === 'left') {
    return (
      <div
        id={id}
        className={cn(
          'grid gap-8 lg:grid-cols-2 lg:items-start lg:gap-16',
          className,
        )}
      >
        <div className="flex flex-col items-start gap-5">
          {overline ? <Overline>{overline}</Overline> : null}
          <h2 className="max-w-[24ch]">{title}</h2>
          {actions ? (
            <div className="mt-2 flex flex-wrap gap-3">{actions}</div>
          ) : null}
        </div>
        {lead ? <p className="mk-lead max-w-[46ch] lg:pt-12">{lead}</p> : null}
      </div>
    )
  }
  return (
    <div
      id={id}
      className={cn(
        'mx-auto flex max-w-3xl flex-col items-center gap-5 text-center',
        className,
      )}
    >
      {overline ? <Overline>{overline}</Overline> : null}
      <h2 className="max-w-[22ch]">{title}</h2>
      {lead ? <p className="mk-lead max-w-[56ch]">{lead}</p> : null}
      {actions ? (
        <div className="mt-2 flex flex-wrap justify-center gap-3">
          {actions}
        </div>
      ) : null}
    </div>
  )
}

type BtnVariant = 'primary' | 'secondary'
type MkButtonProps = {
  variant?: BtnVariant
  arrow?: boolean
  className?: string
  children: ReactNode
}

/** Router link styled as a plane.so button (black pill / outlined with →). */
export function MkLink({
  variant = 'primary',
  arrow,
  className,
  children,
  ...link
}: MkButtonProps & LinkProps) {
  return (
    <Link
      {...link}
      className={cn(
        'mk-btn',
        variant === 'primary' ? 'mk-btn-primary' : 'mk-btn-secondary',
        className,
      )}
    >
      {children}
      {arrow ? <ArrowRight className="size-4" aria-hidden /> : null}
    </Link>
  )
}

/** Same button for plain hrefs (anchors like `#how-it-works`, external docs). */
export function MkAnchor({
  variant = 'primary',
  arrow,
  className,
  children,
  href,
  ...rest
}: MkButtonProps & React.ComponentProps<'a'>) {
  return (
    <a
      href={href}
      {...rest}
      className={cn(
        'mk-btn',
        variant === 'primary' ? 'mk-btn-primary' : 'mk-btn-secondary',
        className,
      )}
    >
      {children}
      {arrow ? <ArrowRight className="size-4" aria-hidden /> : null}
    </a>
  )
}

/** The two hero-style CTAs used all over plane.so: black "Start free" + outlined "… →". */
export function CtaPair({
  primary,
  secondary,
  secondaryTo,
  secondaryHref,
}: {
  primary: string
  secondary: string
  secondaryTo?: LinkProps['to']
  secondaryHref?: string
}) {
  return (
    <>
      <MkLink to="/sign-in" search={{ redirect: '/home' }}>
        {primary}
      </MkLink>
      {secondaryHref ? (
        <MkAnchor href={secondaryHref} variant="secondary" arrow>
          {secondary}
        </MkAnchor>
      ) : (
        <MkLink to={secondaryTo ?? '/consulting'} variant="secondary" arrow>
          {secondary}
        </MkLink>
      )}
    </>
  )
}

/**
 * plane.so "photo frame": a rounded wash (CSS gradient + grain instead of a stock photo) with the product
 * screenshot sitting inside, offset from the top and cropped at the bottom. `tone` picks the wash.
 */
export function Frame({
  src,
  alt,
  width,
  height,
  tone = 'light',
  offset = 'lg',
  className,
  imgClassName,
  eager,
}: {
  src: string
  alt: string
  width: number
  height: number
  tone?: 'light' | 'dark' | 'warm'
  offset?: 'sm' | 'md' | 'lg'
  className?: string
  imgClassName?: string
  eager?: boolean
}) {
  const pad =
    offset === 'sm'
      ? 'pt-6 px-6 sm:pt-8 sm:px-8'
      : offset === 'md'
        ? 'pt-8 px-6 sm:pt-12 sm:px-12'
        : 'pt-10 px-6 sm:pt-16 sm:px-24'
  return (
    <div
      className={cn(
        'mk-frame',
        tone === 'dark' && 'mk-frame--dark',
        tone === 'warm' && 'mk-frame--warm',
        pad,
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        fetchPriority={eager ? 'high' : undefined}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        className={cn('mk-shot', imgClassName)}
      />
    </div>
  )
}

/** Small H3 + paragraph column (plane.so's three-up feature rows). */
export function FeatureCol({
  title,
  children,
  className,
}: {
  title: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <h3>{title}</h3>
      <p className="mk-muted leading-relaxed">{children}</p>
    </div>
  )
}

// ---------------------------------------------------------------- nav

type NavGroup = {
  label: string
  items: Array<{ label: string; description: string; href: string }>
}

function navGroups(): NavGroup[] {
  return [
    {
      label: m.mk_nav_product(),
      items: [
        {
          label: m.mk_nav_workspace(),
          description: m.mk_nav_workspace_desc(),
          href: '/#product',
        },
        {
          label: m.dr_nav(),
          description: m.mk_nav_room_desc(),
          href: '/#room',
        },
        {
          label: m.mk_nav_build(),
          description: m.mk_nav_build_desc(),
          href: '/#build',
        },
        {
          label: m.mk_tb_nav(),
          description: m.mk_tb_nav_desc(),
          href: '/#team-build',
        },
        {
          label: m.mk_nav_agent_team(),
          description: m.mk_nav_agent_team_desc(),
          href: '/#agent-platform',
        },
        {
          label: m.mk_nav_proof(),
          description: m.mk_nav_proof_desc(),
          href: '/#proof',
        },
        {
          label: m.mk_nav_control(),
          description: m.mk_nav_control_desc(),
          href: '/#control',
        },
        {
          label: m.mk_nav_publish(),
          description: m.mk_nav_publish_desc(),
          href: '/#publish',
        },
      ],
    },
    {
      label: m.mk_nav_solutions(),
      items: [
        {
          label: m.simple_nav_mulai(),
          description: m.mk_nav_simple_desc(),
          href: '/mulai',
        },
        {
          label: m.mk_nav_developers(),
          description: m.mk_nav_developers_desc(),
          href: '/#cli',
        },
        {
          label: m.mk_nav_studio(),
          description: m.mk_nav_studio_desc(),
          href: '/studio',
        },
        {
          label: m.mk_nav_campus(),
          description: m.mk_nav_campus_desc(),
          href: '/campus',
        },
        {
          label: m.mk_nav_consulting(),
          description: m.mk_nav_guided_desc(),
          href: '/consulting',
        },
        {
          label: m.mk_foot_templates(),
          description: m.mk_nav_templates_desc(),
          href: '/templates',
        },
      ],
    },
    {
      label: m.mk_nav_resources(),
      items: [
        {
          label: m.tut_nav(),
          description: m.tut_nav_desc(),
          href: '/tutorial',
        },
        {
          label: m.mk_nav_desktop(),
          description: m.mk_nav_desktop_desc(),
          href: '/serve',
        },
        {
          label: m.mk_nav_docs(),
          description: m.mk_nav_docs_desc(),
          href: '/docs',
        },
        {
          label: m.hub_foot_integrations(),
          description: m.mk_nav_integrations_desc(),
          href: '/integrations',
        },
        {
          label: m.rd_foot_link(),
          description: m.mk_nav_readiness_desc(),
          href: '/readiness',
        },
        {
          label: m.mk_foot_benchmark(),
          description: m.mk_nav_benchmark_desc(),
          href: '/benchmark',
        },
        {
          label: m.mk_faq_label(),
          description: m.mk_nav_faq_desc(),
          href: '/#faq',
        },
        {
          label: m.mk_foot_support(),
          description: m.mk_nav_support_desc(),
          href: '/support',
        },
      ],
    },
  ]
}

function LanguageSwitch() {
  const locale = getLocale()
  return (
    <div className="mk-language" role="group" aria-label={m.mk_foot_language()}>
      {(['id', 'en'] as const).map((language) => (
        <button
          key={language}
          type="button"
          lang={language}
          aria-label={language === 'id' ? 'Bahasa Indonesia' : 'English'}
          aria-pressed={locale === language}
          onClick={() => setLocale(language)}
        >
          {language.toUpperCase()}
        </button>
      ))}
    </div>
  )
}

export function MkNav() {
  const { data: session } = useHydratedSession()
  const signedIn = Boolean(session?.user)
  const [open, setOpen] = useState(false)
  const [group, setGroup] = useState<number | null>(null)
  const header = useRef<HTMLElement>(null)
  const mobileTrigger = useRef<HTMLButtonElement>(null)
  const groups = navGroups()
  const close = () => {
    setOpen(false)
    setGroup(null)
  }

  useEffect(() => {
    if (!open && group === null) return
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (open) mobileTrigger.current?.focus()
      else
        header.current
          ?.querySelector<HTMLButtonElement>(`#mk-nav-trigger-${group}`)
          ?.focus()
      setOpen(false)
      setGroup(null)
    }
    const outside = (event: PointerEvent) => {
      if (!header.current?.contains(event.target as Node)) {
        setOpen(false)
        setGroup(null)
      }
    }
    document.addEventListener('keydown', escape)
    document.addEventListener('pointerdown', outside)
    return () => {
      document.removeEventListener('keydown', escape)
      document.removeEventListener('pointerdown', outside)
    }
  }, [open, group])

  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)')
    const reset = () => {
      setOpen(false)
      setGroup(null)
    }
    query.addEventListener('change', reset)
    return () => {
      query.removeEventListener('change', reset)
    }
  }, [])

  return (
    <header
      ref={header}
      className="mk-header sticky top-0 z-40 border-b border-[var(--mk-border)] bg-white"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close()
      }}
    >
      <Container className="mk-nav-bar">
        <Wordmark to="/" />
        <nav className="mk-desktop-nav" aria-label={m.mk_nav_menu()}>
          {groups.map((item, index) => (
            <div key={item.label}>
              <button
                id={`mk-nav-trigger-${index}`}
                type="button"
                className="mk-nav-trigger"
                aria-expanded={group === index}
                aria-controls={`mk-nav-panel-${index}`}
                onClick={() => setGroup(group === index ? null : index)}
              >
                {item.label}
                <ChevronDown className="size-3.5" aria-hidden />
              </button>
              <div
                id={`mk-nav-panel-${index}`}
                className="mk-nav-panel"
                hidden={group !== index}
                aria-labelledby={`mk-nav-trigger-${index}`}
              >
                <div className="mk-nav-panel-grid">
                  {item.items.map((link) => (
                    <a key={link.href} href={link.href} onClick={close}>
                      <span>
                        {link.label}
                        <ArrowRight className="size-4" aria-hidden />
                      </span>
                      <small>{link.description}</small>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <Link to="/pricing" className="mk-nav-trigger">
            {m.mk_nav_pricing()}
          </Link>
        </nav>
        <div className="mk-nav-actions">
          <div className="mk-desktop-language">
            <LanguageSwitch />
          </div>
          {!signedIn ? (
            <Link
              to="/sign-in"
              search={{ redirect: '/home' }}
              className="mk-nav-signin"
            >
              {m.mk_nav_signin()}
            </Link>
          ) : null}
          <MkLink
            to={signedIn ? '/home' : '/sign-in'}
            search={signedIn ? undefined : { redirect: '/home' }}
            className="mk-nav-cta"
          >
            {signedIn ? m.mk_nav_open_app() : m.mk_nav_cta()}
          </MkLink>
          <button
            ref={mobileTrigger}
            type="button"
            className="mk-mobile-trigger"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-controls="mk-mobile-nav"
            aria-label={open ? m.mk_nav_close() : m.mk_nav_menu()}
          >
            {open ? (
              <X className="size-5" aria-hidden />
            ) : (
              <Menu className="size-5" aria-hidden />
            )}
          </button>
        </div>
      </Container>
      <nav
        id="mk-mobile-nav"
        className="mk-mobile-nav"
        hidden={!open}
        aria-label={m.mk_nav_menu()}
      >
        <Container>
          <div className="flex items-center justify-between border-b border-[var(--mk-border)] py-3">
            <span className="text-sm">{m.mk_foot_language()}</span>
            <LanguageSwitch />
          </div>
          {groups.map((item) => (
            <details key={item.label} className="mk-mobile-group">
              <summary>
                {item.label}
                <ChevronDown className="size-4" aria-hidden />
              </summary>
              <div>
                {item.items.map((link) => (
                  <a key={link.href} href={link.href} onClick={close}>
                    {link.label}
                    <ArrowRight className="size-4" aria-hidden />
                  </a>
                ))}
              </div>
            </details>
          ))}
          <Link to="/pricing" onClick={close}>
            {m.mk_nav_pricing()}
          </Link>
          {!signedIn ? (
            <Link to="/sign-in" search={{ redirect: '/home' }} onClick={close}>
              {m.mk_nav_signin()}
            </Link>
          ) : null}
        </Container>
      </nav>
    </header>
  )
}

// ---------------------------------------------------------------- footer

function FootCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm text-[var(--mk-dark-fg-muted)]">{title}</div>
      <ul className="flex flex-col gap-2.5 text-[15px]">{children}</ul>
    </div>
  )
}
function FootLink(props: LinkProps & { children: ReactNode }) {
  return (
    <li>
      <Link
        {...props}
        className="text-[var(--mk-dark-fg)] transition-colors hover:text-white/70"
      />
    </li>
  )
}
function FootAnchor({ href, children }: { href: string; children: ReactNode }) {
  return (
    <li>
      <a
        href={href}
        className="text-[var(--mk-dark-fg)] transition-colors hover:text-white/70"
      >
        {children}
      </a>
    </li>
  )
}

export function MkFooter() {
  const locale = getLocale()
  const { data } = useHydratedSession()
  const change = async (loc: 'en' | 'id') => {
    if (data?.user) {
      try {
        await authClient.updateUser({ locale: loc })
      } catch {}
    }
    setLocale(loc)
  }
  return (
    <footer className="mk-footer mk-dark-section">
      <Container className="py-16 lg:py-20">
        <div className="flex flex-wrap items-start justify-between gap-8">
          <Wordmark to="/" size="lg" />
          <div
            className="flex items-center gap-1 rounded-md border border-[var(--mk-dark-border)] p-1 text-sm"
            role="group"
            aria-label={m.mk_foot_language()}
          >
            {(['en', 'id'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => change(l)}
                aria-pressed={l === locale}
                aria-label={l === 'id' ? 'Bahasa Indonesia' : 'English'}
                className={cn(
                  'rounded px-2.5 py-1 uppercase transition-colors',
                  l === locale
                    ? 'bg-white text-[var(--mk-btn)]'
                    : 'text-[var(--mk-dark-fg-muted)] hover:text-white',
                )}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-10 sm:grid-cols-4">
          <FootCol title={m.mk_foot_product()}>
            <FootLink to="/sign-in" search={{ redirect: '/plan' }}>
              {m.mk_foot_plan()}
            </FootLink>
            <FootAnchor href="/#product">{m.mk_foot_roadmap()}</FootAnchor>
            <FootLink to="/design-room">{m.dr_foot_link()}</FootLink>
            <FootAnchor href="/#proof">{m.mk_foot_proof()}</FootAnchor>
            <FootAnchor href="/#loop">{m.mk_foot_loop()}</FootAnchor>
            <FootAnchor href="/#agent">{m.mk_foot_agent()}</FootAnchor>
            <FootAnchor href="/#agent-platform">
              {m.mk_nav_agent_team()}
            </FootAnchor>
            <FootAnchor href="/#team-build">{m.mk_tb_nav()}</FootAnchor>
            <FootAnchor href="/#cli">{m.mk_foot_cli()}</FootAnchor>
          </FootCol>
          <FootCol title={m.mk_foot_workflow()}>
            <FootLink to="/mulai">{m.simple_nav_mulai()}</FootLink>
            <FootLink to="/otomatis">{m.mk_nav_build()}</FootLink>
            <FootLink to="/sign-in" search={{ redirect: '/plan?flow=new' }}>
              {m.mk_foot_from_idea()}
            </FootLink>
            <FootLink
              to="/sign-in"
              search={{ redirect: '/plan?flow=existing' }}
            >
              {m.mk_foot_from_codebase()}
            </FootLink>
            <FootLink to="/readiness">{m.rd_foot_link()}</FootLink>
            <FootLink to="/studio">{m.st_foot_link()}</FootLink>
            <FootLink to="/campus">{m.cp_foot_link()}</FootLink>
            <FootLink to="/sign-in" search={{ redirect: '/client' }}>
              {m.mk_foot_client()}
            </FootLink>
            <FootLink to="/templates">{m.mk_foot_templates()}</FootLink>
            <FootLink to="/tools">{m.mk_foot_tools()}</FootLink>
            <FootLink to="/coaching">{m.mk_foot_coaching()}</FootLink>
          </FootCol>
          <FootCol title={m.mk_foot_developers()}>
            <FootLink to="/tutorial">{m.tut_foot_link()}</FootLink>
            <FootLink to="/docs">{m.mk_foot_docs()}</FootLink>
            <FootLink to="/docs">{m.mk_foot_cli_docs()}</FootLink>
            <FootLink to="/docs">{m.mk_foot_mcp()}</FootLink>
            <FootLink to="/serve">{m.mk_nav_desktop()}</FootLink>
            <FootLink to="/integrations">{m.hub_foot_integrations()}</FootLink>
            <FootLink to="/benchmark">{m.mk_foot_benchmark()}</FootLink>
            <FootLink to="/compare/$slug" params={{ slug: 'spec-kit' }}>
              {m.mk_foot_compare()}
            </FootLink>
            <FootAnchor href="/api/schema">{m.mk_foot_api()}</FootAnchor>
          </FootCol>
          <FootCol title={m.mk_foot_company()}>
            <FootLink to="/pricing" search={{ voucher: undefined }}>
              {m.mk_foot_pricing()}
            </FootLink>
            <FootLink to="/consulting">{m.mk_foot_consulting()}</FootLink>
            <FootLink to="/support">{m.mk_foot_support()}</FootLink>
            <FootLink to="/feedback">{m.mk_foot_feedback()}</FootLink>
            <FootLink to="/privacy">{m.privacy_title()}</FootLink>
            <FootLink to="/terms">{m.terms_title()}</FootLink>
            <FootLink to="/kebijakan-penyedia">{m.pol_link()}</FootLink>
            <FootLink to="/bot">{m.bot_foot_link()}</FootLink>
            <FootAnchor href="/#faq">{m.mk_faq_label()}</FootAnchor>
          </FootCol>
        </div>
        <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--mk-dark-border)] pt-6 text-sm text-[var(--mk-dark-fg-muted)]">
          <span>
            {m.mk_foot_copyright({ year: String(new Date().getFullYear()) })}
          </span>
          <span>{m.tagline()}</span>
        </div>
      </Container>
    </footer>
  )
}
